import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Textarea } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { useSubmitFeedback, type FeedbackType } from '@/api/feedback';

interface FeedbackDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
    const [type, setType]         = useState<FeedbackType>('improvement');
    const [message, setMessage]   = useState('');
    const [error, setError]       = useState<string | null>(null);
    const [success, setSuccess]   = useState(false);
    const submit = useSubmitFeedback();

    useEffect(() => {
        if (open) {
            setType('improvement');
            setMessage('');
            setError(null);
            setSuccess(false);
        }
    }, [open]);

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        const trimmed = message.trim();
        if (!trimmed) {
            setError('Please enter your feedback before submitting.');
            return;
        }

        try {
            await submit.mutateAsync({
                type,
                message: trimmed,
                pagePath: window.location.pathname,
            });
            setSuccess(true);
            setTimeout(() => onOpenChange(false), 1200);
        } catch (e2) {
            if (e2 instanceof AxiosError) {
                setError(e2.response?.data?.error ?? 'Could not submit feedback.');
            } else {
                setError('Unexpected error.');
            }
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Submit feedback</DialogTitle>
                    <DialogDescription>
                        Share an improvement idea or report a bug.
                    </DialogDescription>
                </DialogHeader>

                {success ? (
                    <Alert variant="success">
                        Thanks — your feedback was submitted.
                    </Alert>
                ) : (
                    <form onSubmit={onSubmit} noValidate>
                        <Alert variant="warning" className="mb-4">
                            <strong>Do not include PHI.</strong> Feedback is
                            routed to a non-clinical inbox for triage.
                            Leave out client names, initials, DOBs,
                            diagnoses, session notes, and any other
                            patient-identifiable detail. Describe the
                            screen and what went wrong in general terms.
                        </Alert>
                        {error && (
                            <Alert variant="danger" className="mb-4">{error}</Alert>
                        )}

                        <FormField label="Type" required>
                            {(id) => (
                                <select
                                    id={id}
                                    value={type}
                                    onChange={(e) => setType(e.target.value as FeedbackType)}
                                    className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm text-text focus:border-accent focus:outline-none transition-colors duration-[var(--duration-base)]"
                                >
                                    <option value="improvement">Suggest an improvement</option>
                                    <option value="bug">Report a bug</option>
                                </select>
                            )}
                        </FormField>

                        <FormField label="Feedback" required>
                            {(id) => (
                                <Textarea
                                    id={id}
                                    rows={6}
                                    maxLength={5000}
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder={
                                        type === 'bug'
                                            ? 'What happened, and what did you expect to happen?'
                                            : 'What would make this part of the app work better for you?'
                                    }
                                />
                            )}
                        </FormField>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => onOpenChange(false)}
                                disabled={submit.isPending}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={submit.isPending || !message.trim()}
                            >
                                {submit.isPending ? 'Sending…' : 'Submit feedback'}
                            </Button>
                        </DialogFooter>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}
